import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { Heart, MessageCircle, ImagePlus, X, Send, User, Trash2, Loader2, UserPlus, MessageSquare, Share2, Facebook, Instagram, Ghost, Link2, Check } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { compressImage } from "../lib/image";
import Messaging from "./Messaging";

export const timeAgo = (iso) => {
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const AuthorLink = ({ name, className = "" }) => (
  <Link to={`/u/${encodeURIComponent(name)}`} className={`hover:underline ${className}`}>
    {name}
  </Link>
);

const Composer = ({ onPosted }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [text, setText] = useState("");
  const [images, setImages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user) {
    return (
      <div className="rounded-md border border-white/10 bg-[#242424] p-5 text-sm text-[#b0b0b0]">
        <Link to="/login" state={{ from: "/community" }} className="font-display uppercase text-[#e0a82e]">Log in</Link> or{" "}
        <Link to="/register" className="font-display uppercase text-[#e0a82e]">register</Link> to post to the community feed.
      </div>
    );
  }

  const handleFiles = async (e) => {
    setError("");
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    if (images.length + files.length > 4) {
      setError("You can attach up to 4 photos.");
      return;
    }
    try {
      const compressed = await Promise.all(files.map((f) => compressImage(f)));
      setImages((prev) => [...prev, ...compressed]);
    } catch (err) {
      setError(err.message || "Could not process image.");
    }
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!text.trim() && images.length === 0) return;
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/feed", { text, images });
      setText("");
      setImages([]);
      onPosted(data);
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not post.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-md border border-white/10 bg-[#242424] p-4">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e0a82e] text-[#1c1c1c]">
          <User className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder={`Share something with the community, ${user.username}...`}
            className="w-full resize-none rounded border border-white/15 bg-[#1e1e1e] px-4 py-2.5 text-[#eee] outline-none focus:border-[#e0a82e]"
          />
          {images.length > 0 && (
            <div className="mt-3 grid grid-cols-3 gap-2 sm:grid-cols-4">
              {images.map((src, i) => (
                <div key={i} className="relative">
                  <img src={src} alt="upload preview" className="h-24 w-full rounded object-cover" />
                  <button
                    onClick={() => setImages(images.filter((_, idx) => idx !== i))}
                    className="absolute right-1 top-1 rounded-full bg-black/70 p-1 text-white hover:bg-black"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
            </div>
          )}
          {error && <p className="mt-2 text-sm text-[#f08a80]">{error}</p>}
          <div className="mt-3 flex items-center justify-between">
            <button
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-2 rounded px-3 py-2 font-display text-sm uppercase tracking-wide text-[#e0a82e] transition-colors hover:bg-[#333]"
            >
              <ImagePlus className="h-4 w-4" /> Photo
            </button>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} />
            <button
              onClick={submit}
              disabled={busy || (!text.trim() && images.length === 0)}
              className="inline-flex items-center gap-2 rounded bg-[#e0a82e] px-5 py-2 font-display text-sm font-600 uppercase tracking-wide text-[#1c1c1c] transition-colors hover:bg-[#f2b93f] disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Post
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ShareMenu = ({ post }) => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState("");
  const ref = useRef(null);
  const url = `${window.location.origin}/community`;
  const text = (post.text || "Check out this post on the Incarcerated community.").trim();
  const full = `${text}\n\n${url}`;

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Incarcerated", text, url }); } catch (e) { /* cancelled */ }
      return true;
    }
    return false;
  };

  const copy = async (label) => {
    try { await navigator.clipboard.writeText(full); } catch (e) { /* ignore */ }
    setCopied(label);
    setTimeout(() => setCopied(""), 2500);
  };

  const toFacebook = () => {
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
      "_blank", "noopener,noreferrer,width=640,height=640"
    );
    setOpen(false);
  };
  const toInstagram = async () => {
    if (await nativeShare()) { setOpen(false); return; }
    await copy("instagram");
    window.open("https://www.instagram.com/", "_blank", "noopener,noreferrer");
  };
  const toSnapchat = async () => {
    if (await nativeShare()) { setOpen(false); return; }
    await copy("snapchat");
    window.open("https://www.snapchat.com/", "_blank", "noopener,noreferrer");
  };

  const itemCls = "flex w-full items-center gap-3 rounded px-3 py-2 text-sm text-[#e8e8e8] transition-colors hover:bg-[#2a2a2a]";

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`share-btn-${post.id}`}
        className="flex items-center gap-1.5 text-sm text-[#c0c0c0] transition-colors hover:text-[#e0a82e]"
      >
        <Share2 className="h-5 w-5" /> Share
      </button>
      {open && (
        <div className="absolute top-full left-0 z-30 mt-2 w-60 rounded-md border border-white/10 bg-[#1c1c1c] p-1.5 shadow-xl" data-testid={`share-menu-${post.id}`}>
          <button onClick={toFacebook} data-testid={`share-facebook-${post.id}`} className={itemCls}>
            <Facebook className="h-4 w-4 text-[#1877f2]" /> Facebook
          </button>
          <button onClick={toInstagram} data-testid={`share-instagram-${post.id}`} className={itemCls}>
            <Instagram className="h-4 w-4 text-[#e1306c]" /> Instagram
          </button>
          <button onClick={toSnapchat} data-testid={`share-snapchat-${post.id}`} className={itemCls}>
            <Ghost className="h-4 w-4 text-[#f3cf00]" /> Snapchat
          </button>
          <button onClick={() => copy("link")} data-testid={`share-copy-${post.id}`} className={itemCls}>
            {copied ? <Check className="h-4 w-4 text-[#4caf7d]" /> : <Link2 className="h-4 w-4 text-[#e0a82e]" />} Copy link &amp; text
          </button>
          {copied === "instagram" && <p className="px-3 py-1.5 text-xs text-[#9fe0bf]">Caption copied — paste it into your Instagram post.</p>}
          {copied === "snapchat" && <p className="px-3 py-1.5 text-xs text-[#9fe0bf]">Caption copied — paste it into your Snap.</p>}
          {copied === "link" && <p className="px-3 py-1.5 text-xs text-[#9fe0bf]">Copied to clipboard.</p>}
        </div>
      )}
    </div>
  );
};

export const CommentBody = ({ c, onLike, onReply, onDelete, small }) => (
  <div id={`comment-${c.id}`} data-testid={`comment-${c.id}`} className={`rounded-md bg-[#1c1c1c] ${small ? "p-2.5" : "p-3"}`}>
    <div className="flex items-center gap-2 text-xs text-[#8f8f8f]">
      <AuthorLink name={c.author} className="text-[#e0a82e]" />
      <span>{timeAgo(c.createdAt)}</span>
    </div>
    <p className="mt-1 whitespace-pre-wrap text-sm text-[#d0d0d0]">{c.body}</p>
    <div className="mt-1.5 flex items-center gap-4">
      <button
        onClick={() => onLike(c)}
        data-testid={`comment-like-${c.id}`}
        title={c.likedByMe ? "Unlike" : "Like"}
        className={`flex items-center gap-1 text-xs transition-colors ${c.likedByMe ? "text-[#e0574c]" : "text-[#8f8f8f] hover:text-[#e0574c]"}`}
      >
        <Heart className={`h-4 w-4 transition-transform active:scale-125 ${c.likedByMe ? "fill-[#e0574c]" : ""}`} />
        <span data-testid={`comment-like-count-${c.id}`}>{c.likes || 0}</span>
      </button>
      <button onClick={() => onReply(c)} data-testid={`comment-reply-btn-${c.id}`} className="text-xs uppercase tracking-wide text-[#8f8f8f] transition-colors hover:text-[#e0a82e]">
        Reply
      </button>
      {onDelete && (
        <button onClick={() => onDelete(c)} data-testid={`comment-delete-${c.id}`} title="Remove" className="ml-auto text-[#8f8f8f] transition-colors hover:text-[#e0574c]">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  </div>
);

export const CommentThread = ({ comment, replies, onLike, onReply, onDelete }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [target, setTarget] = useState(null);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const hidden = expanded ? 0 : Math.max(0, replies.length - 2);
  const shown = hidden ? replies.slice(0, 2) : replies;

  const startReply = (c) => {
    if (!user) return navigate("/login", { state: { from: window.location.pathname } });
    setTarget(c);
    setText(c.id !== comment.id ? `@${c.author} ` : "");
  };

  const send = async () => {
    if (!text.trim()) return;
    setSending(true);
    try {
      if (await onReply(target, text)) { setTarget(null); setText(""); setExpanded(true); }
    } finally {
      setSending(false);
    }
  };

  return (
    <div data-testid={`comment-thread-${comment.id}`}>
      <CommentBody c={comment} onLike={onLike} onReply={startReply} onDelete={onDelete} />
      {(replies.length > 0 || target) && (
        <div className="ml-6 mt-2 space-y-2 border-l-2 border-white/10 pl-3 md:ml-8">
          {shown.map((r) => (
            <CommentBody key={r.id} c={r} onLike={onLike} onReply={startReply} onDelete={onDelete} small />
          ))}
          {hidden > 0 && (
            <button onClick={() => setExpanded(true)} data-testid={`view-more-replies-${comment.id}`} className="text-xs font-600 uppercase tracking-wide text-[#e0a82e] transition-colors hover:text-[#f2b93f]">
              View {hidden} more {hidden === 1 ? "reply" : "replies"}
            </button>
          )}
          {target && (
            <div className="flex items-center gap-2">
              <input
                autoFocus
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") send(); if (e.key === "Escape") setTarget(null); }}
                placeholder={`Reply to ${target.author}...`}
                data-testid={`reply-input-${comment.id}`}
                className="flex-1 rounded border border-white/15 bg-[#1e1e1e] px-3 py-1.5 text-sm text-[#eee] outline-none focus:border-[#e0a82e]"
              />
              <button onClick={send} disabled={sending} data-testid={`reply-send-${comment.id}`} className="rounded bg-[#e0a82e] p-1.5 text-[#1c1c1c] hover:bg-[#f2b93f] disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
              <button onClick={() => setTarget(null)} data-testid={`reply-cancel-${comment.id}`} className="p-1.5 text-[#8f8f8f] hover:text-white">
                <X className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PostCard = ({ post, onChanged, onDeleted, onMessage, onAddFriend, focused }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);

  const toggleComments = async () => {
    const next = !showComments;
    setShowComments(next);
    if (next && !loaded) {
      const { data } = await api.get(`/feed/${post.id}/comments`);
      setComments(data);
      setLoaded(true);
    }
  };

  const like = async () => {
    if (!user) return navigate("/login", { state: { from: "/community" } });
    const { data } = await api.post(`/feed/${post.id}/like`);
    onChanged({ ...post, likes: data.likes, likedByMe: data.likedByMe });
  };

  const likeComment = async (c) => {
    if (!user) return navigate("/login", { state: { from: "/community" } });
    const { data } = await api.post(`/feed/comments/${c.id}/like`);
    setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...data } : x)));
  };

  useEffect(() => {
    if (!focused) return;
    (async () => {
      if (!loaded) {
        const { data } = await api.get(`/feed/${post.id}/comments`);
        setComments(data);
        setLoaded(true);
      }
      setShowComments(true);
      setTimeout(() => document.getElementById(`post-${post.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  const addComment = async (body = commentText, parentId = null) => {
    if (!body.trim()) return false;
    if (!user) { navigate("/login", { state: { from: "/community" } }); return false; }
    setBusy(true);
    try {
      const { data } = await api.post(`/feed/${post.id}/comments`, { body, parent_id: parentId });
      setComments((c) => [...c, data]);
      if (!parentId) setCommentText("");
      onChanged({ ...post, comments: (post.comments || 0) + 1 });
      return true;
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(post.author_id === user?.id ? "Delete this post?" : `Delete this post by ${post.author}? This removes it and all its comments.`)) return;
    await api.delete(`/feed/${post.id}`);
    onDeleted(post.id);
  };

  const removeComment = async (c) => {
    if (!window.confirm(`Delete this comment by ${c.author}${c.parent_id ? "" : " and its replies"}?`)) return;
    const { data } = await api.delete(`/feed/comments/${c.id}`);
    setComments((prev) => prev.filter((x) => x.id !== c.id && x.parent_id !== c.id));
    onChanged({ ...post, comments: Math.max(0, (post.comments || 0) - (data.removed || 1)) });
  };

  const canDelete = user && (post.author_id === user.id || user.is_admin);

  return (
    <div id={`post-${post.id}`} data-testid={`feed-post-${post.id}`} className={`scroll-mt-24 rounded-md border bg-[#242424] p-5 transition-colors duration-700 ${focused ? "border-[#e0a82e]" : "border-white/10"}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[#333] text-[#e0a82e]">
            <User className="h-5 w-5" />
          </div>
          <div>
            <AuthorLink name={post.author} className="font-display text-base font-500 text-[#f0f0f0]" />
            <p className="text-xs text-[#8f8f8f]">{timeAgo(post.createdAt)}</p>
          </div>
        </div>
        {user && post.author_id === user.id ? (
          <button onClick={remove} data-testid={`post-delete-${post.id}`} className="rounded p-2 text-[#8f8f8f] transition-colors hover:text-[#e0574c]">
            <Trash2 className="h-4 w-4" />
          </button>
        ) : user ? (
          <div className="flex gap-1">
            {canDelete && (
              <button onClick={remove} data-testid={`post-delete-${post.id}`} title="Delete post (admin)" className="rounded p-2 text-[#8f8f8f] transition-colors hover:text-[#e0574c]">
                <Trash2 className="h-4 w-4" />
              </button>
            )}
            <button onClick={() => onAddFriend(post.author)} title="Add friend" className="inline-flex items-center gap-1 rounded border border-white/15 px-2.5 py-1.5 text-xs uppercase text-[#c0c0c0] transition-colors hover:border-[#e0a82e] hover:text-[#e0a82e]">
              <UserPlus className="h-3.5 w-3.5" /> Add
            </button>
            <button onClick={() => onMessage(post.author)} title="Message" className="inline-flex items-center gap-1 rounded border border-white/15 px-2.5 py-1.5 text-xs uppercase text-[#c0c0c0] transition-colors hover:border-[#e0a82e] hover:text-[#e0a82e]">
              <MessageSquare className="h-3.5 w-3.5" /> Message
            </button>
          </div>
        ) : null}
      </div>

      {post.text && <p className="mt-3 whitespace-pre-wrap leading-relaxed text-[#d8d8d8]">{post.text}</p>}

      {post.images?.length > 0 && (
        <div className={`mt-3 grid gap-2 ${post.images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}>
          {post.images.map((src, i) => (
            <a key={i} href={src} target="_blank" rel="noopener noreferrer">
              <img src={src} alt="post" className="max-h-96 w-full rounded-md object-cover" loading="lazy" />
            </a>
          ))}
        </div>
      )}

      <div className="mt-4 flex items-center gap-4 border-t border-white/10 pt-3">
        <button
          onClick={like}
          className={`flex items-center gap-1.5 text-sm transition-colors ${post.likedByMe ? "text-[#e0574c]" : "text-[#c0c0c0] hover:text-[#e0574c]"}`}
        >
          <Heart className={`h-5 w-5 ${post.likedByMe ? "fill-[#e0574c]" : ""}`} /> {post.likes}
        </button>
        <button onClick={toggleComments} className="flex items-center gap-1.5 text-sm text-[#c0c0c0] transition-colors hover:text-[#e0a82e]">
          <MessageCircle className="h-5 w-5" /> {post.comments}
        </button>
        <ShareMenu post={post} />
      </div>

      {showComments && (
        <div className="mt-4 space-y-3">
          {comments.filter((c) => !c.parent_id).map((c) => (
            <CommentThread
              key={c.id}
              comment={c}
              replies={comments.filter((r) => r.parent_id === c.id)}
              onLike={likeComment}
              onReply={(target, body) => addComment(body, target.id)}
              onDelete={user?.is_admin ? removeComment : undefined}
            />
          ))}
          {user ? (
            <div className="flex items-center gap-2">
              <input
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Write a comment..."
                className="flex-1 rounded border border-white/15 bg-[#1e1e1e] px-3 py-2 text-sm text-[#eee] outline-none focus:border-[#e0a82e]"
              />
              <button onClick={() => addComment()} disabled={busy} className="rounded bg-[#e0a82e] p-2 text-[#1c1c1c] hover:bg-[#f2b93f] disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <p className="text-xs text-[#9a9a9a]">
              <Link to="/login" state={{ from: "/community" }} className="text-[#e0a82e]">Log in</Link> to join the conversation.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

const Feed = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const msgRef = useRef(null);

  useEffect(() => {
    api.get("/feed").then(({ data }) => {
      setPosts(data);
      setLoading(false);
    });
  }, []);

  const location = useLocation();
  const focusId = !loading && location.hash.startsWith("#post-") ? location.hash.slice(6) : null;

  const onPosted = (p) => setPosts((prev) => [p, ...prev]);
  const onChanged = (updated) => setPosts((prev) => prev.map((p) => (p.id === updated.id ? updated : p)));
  const onDeleted = (id) => setPosts((prev) => prev.filter((p) => p.id !== id));
  const onMessage = (username) => msgRef.current?.openThread(username);
  const onAddFriend = (username) => {
    msgRef.current?.addFriend(username);
    msgRef.current?.openFriends();
  };

  return (
    <div className="mt-6 space-y-4">
      <Messaging ref={msgRef} />
      <Composer onPosted={onPosted} />
      {loading ? (
        <p className="py-10 text-center text-[#9a9a9a]">Loading feed...</p>
      ) : posts.length === 0 ? (
        <p className="py-10 text-center text-[#9a9a9a]">No posts yet. Be the first to share something.</p>
      ) : (
        posts.map((p) => (
          <PostCard key={p.id} post={p} onChanged={onChanged} onDeleted={onDeleted} onMessage={onMessage} onAddFriend={onAddFriend} focused={focusId === p.id} />
        ))
      )}
    </div>
  );
};

export default Feed;
